import axios, { AxiosInstance } from "axios";
import type {
  User,
  Machine,
  AlertRule,
  Location,
  Tenant,
  CreateTenantRequest,
  HardwareData,
  WebHistory,
  SuperAdminStats,
  TenantStatistics,
  Branch,
  ProhibitedSite,
  InstalledProgram,
  Device,
  MachineWithLocation,
  OpenApplication,
} from "../types";

// Helper function to get API base URL from environment variables
// React Scripts only exposes variables starting with REACT_APP_
// The prebuild script converts VITE_API_URL to REACT_APP_API_URL
// Also supports runtime injection via window.__API_URL__
export function getApiBaseUrl(): string {
  // 1. Check runtime injection (from index.html script tag)
  if (typeof window !== "undefined") {
    let injectedUrl = (window as any).__API_URL__;

    // If not found or is localhost, try to read from script tag in DOM
    if (
      !injectedUrl ||
      injectedUrl === "http://127.0.0.1:8000" ||
      injectedUrl.includes("127.0.0.1")
    ) {
      try {
        const scripts = document.getElementsByTagName("script");
        for (let i = 0; i < scripts.length; i++) {
          const scriptContent =
            scripts[i].innerHTML || scripts[i].textContent || "";
          const urlMatch = scriptContent.match(
            /window\.__API_URL__\s*=\s*['"](https?:\/\/[^'"]+)['"]/
          );
          if (urlMatch && urlMatch[1]) {
            injectedUrl = urlMatch[1];
            (window as any).__API_URL__ = injectedUrl;
            break;
          }
        }
      } catch (e) {
        // Silent fallback
      }
    }

    // Check if we have a valid URL
    if (injectedUrl && typeof injectedUrl === "string") {
      const trimmedUrl = injectedUrl.trim();

      // Forçar HTTPS em produção (não permitir HTTP para URLs remotas)
      if (
        trimmedUrl.startsWith("http://") &&
        !trimmedUrl.includes("127.0.0.1") &&
        !trimmedUrl.includes("localhost")
      ) {
        // Converter HTTP para HTTPS automaticamente
        const httpsUrl = trimmedUrl.replace("http://", "https://");
        return httpsUrl;
      }

      // Use any valid HTTPS URL (production)
      if (
        trimmedUrl.startsWith("https://") &&
        trimmedUrl !== "http://127.0.0.1:8000"
      ) {
        return trimmedUrl;
      }

      // Permitir HTTP apenas para localhost (desenvolvimento local)
      if (
        trimmedUrl.startsWith("http://") &&
        (trimmedUrl.includes("127.0.0.1") || trimmedUrl.includes("localhost"))
      ) {
        return trimmedUrl;
      }
    }
  }

  // 2. React Scripts exposes REACT_APP_* variables at build time
  if (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) {
    const envUrl = process.env.REACT_APP_API_URL;
    if (
      envUrl &&
      envUrl !== "http://127.0.0.1:8000" &&
      !envUrl.includes("127.0.0.1")
    ) {
      // Forçar HTTPS em produção
      if (envUrl.startsWith("http://") && !envUrl.includes("localhost")) {
        const httpsUrl = envUrl.replace("http://", "https://");
        return httpsUrl;
      }
      return envUrl;
    }
  }

  // 3. Default fallback for local development
  return "http://127.0.0.1:8000";
}

class ApiService {
  private _api: AxiosInstance | null = null;
  private _lastApiUrl: string | null = null;

  private get api(): AxiosInstance {
    // Read URL dynamically every time (in case it changes)
    const apiBaseUrl = getApiBaseUrl();

    // Recreate axios instance if URL changed
    if (!this._api || this._lastApiUrl !== apiBaseUrl) {
      this._lastApiUrl = apiBaseUrl;

      if (!apiBaseUrl) {
        throw new Error("API base URL não configurada");
      }

      this._api = axios.create({
        baseURL: `${apiBaseUrl}/api`,
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Add request interceptor to include auth token
      this._api.interceptors.request.use(
        (config) => {
          const token = localStorage.getItem("auth_token");
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );

      // Add response interceptor for error handling
      this._api.interceptors.response.use(
        (response) => {
          return response;
        },
        (error) => {
          // Não redirecionar se já estiver na página de login ou se for erro de login
          const isLoginRequest = error.config?.url?.includes("/auth/login");
          const isLoginPage = window.location.pathname === "/login";

          if (
            error.response?.status === 401 &&
            !isLoginRequest &&
            !isLoginPage
          ) {
            // Apenas limpar tokens e redirecionar se não for uma requisição de login
            localStorage.removeItem("auth_token");
            localStorage.removeItem("user");
            window.location.href = "/login";
          }
          return Promise.reject(error);
        }
      );
    }
    return this._api;
  }

  // Authentication
  async login(
    email: string,
    password: string
  ): Promise<{ user: User; token: string }> {
    const response = await this.api.post("/auth/login", { email, password });
    // Backend retorna { success: true, user: {...}, token: "..." }
    // Ou { success: false, message: "..." } em caso de erro
    if (response.data.success && response.data.user && response.data.token) {
      return {
        user: response.data.user,
        token: response.data.token,
      };
    }
    // Se não tiver success, pode ser formato antigo (compatibilidade)
    if (response.data.user && response.data.token) {
      return {
        user: response.data.user,
        token: response.data.token,
      };
    }
    // Se chegou aqui, algo está errado
    throw new Error(response.data.message || "Erro ao fazer login");
  }

  async logout(): Promise<void> {
    await this.api.post("/auth/logout");
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get("/auth/me");
    return response.data;
  }

  async changePassword(data: {
    new_password: string;
    new_password_confirmation: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post("/auth/change-password", data);
    return response.data;
  }

  // Dashboard
  async getDashboardStats(): Promise<any> {
    const response = await this.api.get("/dashboard/overview");
    return response.data;
  }

  async getMachinesForMap(tenantId?: string): Promise<MachineWithLocation[]> {
    const params = tenantId ? { tenant_id: tenantId } : {};
    const response = await this.api.get("/dashboard/machines-map", { params });
    return response.data.data || [];
  }

  async getMachinesByBranch(): Promise<{
    success: boolean;
    data: Array<{
      branch_id: string | null;
      branch_name: string;
      total: number;
      online: number;
      offline: number;
    }>;
  }> {
    const response = await this.api.get("/dashboard/machines-by-branch");
    return response.data;
  }

  // Alerts
  async getAlerts(params?: {
    status?: string;
    severity?: string;
    type?: string;
    machine_id?: string;
    page?: number;
    limit?: number;
  }): Promise<any> {
    // Backend: GET /alerts -> AlertController@index
    // Retorno esperado (conforme AlertsPage e DashboardPage):
    // { success: true, data: { alerts: [], pagination: {} } }
    const response = await this.api.get("/alerts", {
      params: {
        // O backend usa "per_page" para paginação; mapeamos a partir de "limit" do frontend
        per_page: params?.limit,
        page: params?.page,
        status: params?.status,
        severity: params?.severity,
        type: params?.type,
        machine_id: params?.machine_id,
      },
    });
    return response.data;
  }

  // Machines
  async getMachines(params?: {
    search?: string;
    status?: string;
    branch?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: Machine[]; total: number; page: number; limit: number }> {
    const response = await this.api.get("/machines", { params });
    // Backend retorna { success: true, data: [...], pagination: { total, ... } }
    if (response.data && response.data.pagination) {
      return {
        data: response.data.data || [],
        total: response.data.pagination.total || 0,
        page: response.data.pagination.current_page || 1,
        limit: response.data.pagination.per_page || 15
      };
    }
    // Fallback para estrutura antiga (se houver)
    return response.data;
  }

  async getMachine(id: string): Promise<Machine> {
    const response = await this.api.get(`/machines/${id}`);
    return response.data.data;
  }

  async getMachineDetails(id: string): Promise<{
    machine: Machine;
    current_hardware: any;
    hardware_changes: any;
    storage_devices: any[];
    web_history: any;
    location_history: any;
    alerts: any;
  }> {
    const response = await this.api.get(`/machine/${id}/details`);
    return response.data.data;
  }

  async getMachineHardware(id: string): Promise<HardwareData[]> {
    const response = await this.api.get(`/machine/${id}/hardware`);
    return response.data.data;
  }

  async getMachineLocation(id: string): Promise<Location[]> {
    const response = await this.api.get(`/machine/${id}/location`);
    return response.data.data;
  }

  async updateMachine(id: string, data: any): Promise<Machine> {
    const response = await this.api.put(`/machines/${id}`, data);
    return response.data.data;
  }

  async updateMachineBranch(
    id: string,
    branchId: string | null
  ): Promise<Machine> {
    const response = await this.api.put(`/machines/${id}/branch`, {
      branch_id: branchId,
    });
    return response.data.data;
  }

  async recalculateMachineLocation(
    id: string,
    service: string
  ): Promise<{ success: boolean; message?: string; method?: string; location?: any }> {
    const response = await this.api.post(
      `/machines/${id}/recalculate-location`,
      { service }
    );
    return response.data;
  }

  async forceSyncMachine(
    id: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post(`/machines/${id}/force-sync`);
    return response.data;
  }

  async updateMachineSyncInterval(
    id: string,
    syncInterval: number
  ): Promise<{
    success: boolean;
    message: string;
    data: { sync_interval: number };
  }> {
    const response = await this.api.put(`/machines/${id}/sync-interval`, {
      sync_interval: syncInterval,
    });
    return response.data;
  }

  async deleteMachine(id: string): Promise<void> {
    await this.api.delete(`/machines/${id}`);
  }

  async getTrashedMachines(): Promise<{ data: Machine[] }> {
    const response = await this.api.get("/machines/trash/list");
    return response.data;
  }

  async restoreMachine(id: string): Promise<void> {
    await this.api.post(`/machines/${id}/restore`);
  }

  async forceDeleteMachine(id: string): Promise<void> {
    await this.api.delete(`/machines/${id}/force-delete`);
  }

  async getMachineInstalledPrograms(id: string): Promise<InstalledProgram[]> {
    const response = await this.api.get(`/machine/${id}/programs`);
    return response.data;
  }

  async getMachineDevices(id: string): Promise<Device[]> {
    const response = await this.api.get(`/machine/${id}/devices`);
    return response.data;
  }

  async getMachineWebHistory(
    id: string,
    params?: {
      search?: string;
      status?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<{
    data: WebHistory[];
    total: number;
    page: number;
    limit: number;
  }> {
    const response = await this.api.get(`/machine/${id}/web-history`, {
      params,
    });
    return response.data;
  }

  async getMachineOpenApplications(
    id: string,
    params?: {
      start_date?: string;
      end_date?: string;
      process_name?: string;
      window_title?: string;
      page?: number;
      per_page?: number;
    }
  ): Promise<{
    success: boolean;
    data: OpenApplication[];
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      from: number | null;
      to: number | null;
    };
  }> {
    const response = await this.api.get(`/machine/${id}/open-applications`, {
      params,
    });
    return response.data;
  }

  async acknowledgeAlert(id: string): Promise<void> {
    await this.api.post(`/alerts/${id}/acknowledge`);
  }

  async resolveAlert(id: string, resolution?: string): Promise<void> {
    await this.api.post(`/alerts/${id}/resolve`, { resolution });
  }

  async getAlertRules(): Promise<{ success: boolean; data: AlertRule[] }> {
    const response = await this.api.get("/company/alert-rules");
    return response.data;
  }

  async updateAlertRules(
    rules: AlertRule[]
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.api.put("/company/alert-rules", { rules });
    return response.data;
  }

  // Super Admin (Tenant Management)
  async getTenant(id: string): Promise<{ success: boolean; data: Tenant }> {
    const response = await this.api.get(`/super/tenants/${id}`);
    return response.data;
  }

  async getTenants(params?: {
    search?: string;
    status?: string;
    sort_by?: string;
    sort_order?: "asc" | "desc";
    page?: number;
    per_page?: number;
  }): Promise<{
    data: Tenant[];
    pagination: { total: number; current_page: number; per_page: number };
  }> {
    const response = await this.api.get("/super/tenants", { params });
    return response.data;
  }

  async createTenant(
    data: CreateTenantRequest
  ): Promise<{
    success: boolean;
    message: string;
    data: { tenant: Tenant; admin_user: User };
  }> {
    const response = await this.api.post("/super/tenants", data);
    return response.data;
  }

  async updateTenant(
    id: string,
    data: {
      name?: string;
      status?: "active" | "inactive" | "suspended";
      admin_phone?: string;
      admin_email?: string;
      address_street?: string;
      address_number?: string;
      address_city?: string;
      address_state?: string;
      address_zip?: string;
      max_machines?: number | null;
    }
  ): Promise<{ success: boolean; data: Tenant; message: string }> {
    const response = await this.api.put(`/super/tenants/${id}`, data);
    return response.data;
  }

  async updateTenantStatus(
    id: string,
    status: "active" | "inactive" | "suspended"
  ): Promise<{ success: boolean; data: Tenant; message: string }> {
    const response = await this.api.patch(`/super/tenants/${id}/status`, {
      status,
    });
    return response.data;
  }

  async deleteTenant(id: string): Promise<void> {
    await this.api.delete(`/super/tenants/${id}`);
  }

  async validateTenantIsolation(id: string): Promise<any> {
    const response = await this.api.get(
      `/super/tenants/${id}/validate-isolation`
    );
    return response.data;
  }

  async uploadTenantLogo(
    id: string,
    file: File
  ): Promise<{ success: boolean; url: string }> {
    const formData = new FormData();
    formData.append("logo", file);
    const response = await this.api.post(
      `/super/tenants/${id}/logo`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async uploadTenantBackground(
    id: string,
    file: File
  ): Promise<{ success: boolean; url: string }> {
    const formData = new FormData();
    formData.append("background", file);
    const response = await this.api.post(
      `/super/tenants/${id}/background`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async uploadTenantContract(
    id: string,
    file: File,
    expirationDate?: string
  ): Promise<{ success: boolean; url: string }> {
    const formData = new FormData();
    formData.append("contract", file);
    if (expirationDate) {
      formData.append("expiration_date", expirationDate);
    }
    const response = await this.api.post(
      `/super/tenants/${id}/contract`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async getTenantMachines(id: string, params?: any): Promise<any> {
    const response = await this.api.get(`/super/tenants/${id}/machines`, {
      params,
    });
    return response.data;
  }

  async getTenantUsers(id: string, params?: any): Promise<any> {
    const response = await this.api.get(`/super/tenants/${id}/users`, {
      params,
    });
    return response.data;
  }

  async updateTenantUser(
    tenantId: string,
    userId: string,
    data: any
  ): Promise<any> {
    const response = await this.api.put(
      `/super/tenants/${tenantId}/users/${userId}`,
      data
    );
    return response.data;
  }

  async createTenantUser(tenantId: string, data: any): Promise<any> {
    const response = await this.api.post(
      `/super/tenants/${tenantId}/users`,
      data
    );
    return response.data;
  }

  async getTenantBranches(id: string): Promise<any> {
    const response = await this.api.get(`/super/tenants/${id}/branches`);
    return response.data;
  }

  async getTenantAlerts(id: string, params?: any): Promise<any> {
    const response = await this.api.get(`/super/tenants/${id}/alerts`, {
      params,
    });
    return response.data;
  }

  async getSuperAdminOverview(): Promise<{ data: SuperAdminStats }> {
    const response = await this.api.get("/super/overview");
    return response.data;
  }

  async getSuperAdminStatistics(): Promise<SuperAdminStats> {
    const response = await this.api.get("/super/overview");
    return response.data.data;
  }

  async getTenantStatistics(id: string): Promise<TenantStatistics> {
    const response = await this.api.get(`/super/tenants/${id}`);
    return response.data.data.statistics;
  }

  // Logs management (super admin only)
  async getLogs(params?: {
    lines?: number;
    level?: "all" | "ERROR" | "WARNING" | "INFO" | "DEBUG";
    search?: string;
  }): Promise<{
    logs: Array<{
      raw: string;
      timestamp: string | null;
      level: string;
      message: string;
    }>;
    total_lines: number;
    file: string;
    file_path: string;
    file_size: number;
  }> {
    const response = await this.api.get("/logs", { params });
    return response.data.data;
  }

  async getErrorLogs(): Promise<{
    logs: Array<{
      raw: string;
      timestamp: string | null;
      level: string;
      message: string;
    }>;
    total_lines: number;
    file: string;
    file_path: string;
    file_size: number;
  }> {
    const response = await this.api.get("/logs/errors");
    return response.data.data;
  }

  async downloadLogFile(): Promise<Blob> {
    const response = await this.api.get("/logs/download", {
      responseType: "blob",
    });
    return response.data;
  }

  // Company Settings
  async getCompanySettings(): Promise<{ success: boolean; data: any }> {
    const response = await this.api.get("/company/settings");
    return response.data; // Backend retorna { success: true, data: {...} }
  }

  async updateCompanySettings(
    data: any
  ): Promise<{ success: boolean; data: any; message: string }> {
    const response = await this.api.put("/company/settings", data);
    return response.data;
  }

  async uploadCompanyLogo(
    file: File
  ): Promise<{ success: boolean; data: { logo: string; logo_url: string } }> {
    const formData = new FormData();
    formData.append("logo", file);

    const response = await this.api.post("/company/settings/logo", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  }

  async uploadCompanyBackground(
    file: File
  ): Promise<{
    success: boolean;
    data: { background: string; background_url: string };
  }> {
    const formData = new FormData();
    formData.append("background", file);

    const response = await this.api.post(
      "/company/settings/background",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  }

  // Company Users Management
  async getCompanyUsers(): Promise<{ success: boolean; data: User[] }> {
    const response = await this.api.get("/company/users");
    return response.data;
  }

  async createCompanyUser(
    data: any
  ): Promise<{ success: boolean; data: User }> {
    const response = await this.api.post("/company/users", data);
    return response.data;
  }

  async updateCompanyUser(
    id: string,
    data: any
  ): Promise<{ success: boolean; data: User }> {
    const response = await this.api.put(`/company/users/${id}`, data);
    return response.data;
  }

  async deleteCompanyUser(id: string): Promise<{ success: boolean }> {
    const response = await this.api.delete(`/company/users/${id}`);
    return response.data;
  }

  // Branch Management
  async getBranches(params?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    success: boolean;
    data: Branch[];
    total: number;
    page: number;
    limit: number;
  }> {
    const response = await this.api.get("/company/branches", { params });
    // Normalize response to always have success property if missing, assuming success if data exists
    if (response.data.success === undefined && response.data.data) {
      return { success: true, ...response.data };
    }
    return response.data;
  }

  async createBranch(data: {
    name: string;
    location?: string;
  }): Promise<{ success: boolean; data: Branch; message: string }> {
    const response = await this.api.post("/company/branches", data);
    return response.data;
  }

  async updateBranch(
    id: string,
    data: { name?: string; location?: string }
  ): Promise<{ success: boolean; data: Branch; message: string }> {
    const response = await this.api.put(`/company/branches/${id}`, data);
    return response.data;
  }

  async deleteBranch(
    id: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/company/branches/${id}`);
    return response.data;
  }

  // Prohibited Sites
  async getProhibitedSites(): Promise<{
    success: boolean;
    data: ProhibitedSite[];
  }> {
    const response = await this.api.get("/company/prohibited-sites");
    return response.data;
  }

  async createProhibitedSite(data: {
    url_pattern: string;
    description?: string;
  }): Promise<{ success: boolean; data: ProhibitedSite; message: string }> {
    const response = await this.api.post("/company/prohibited-sites", data);
    return response.data;
  }

  async updateProhibitedSite(
    id: string,
    data: { url_pattern?: string; description?: string; is_active?: boolean }
  ): Promise<{ success: boolean; data: ProhibitedSite; message: string }> {
    const response = await this.api.put(
      `/company/prohibited-sites/${id}`,
      data
    );
    return response.data;
  }

  async deleteProhibitedSite(
    id: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/company/prohibited-sites/${id}`);
    return response.data;
  }

  async updateProhibitedSitesSettings(data: {
    block_prohibited_sites: boolean;
  }): Promise<any> {
    const response = await this.api.put(
      "/company/settings/prohibited-sites",
      data
    );
    return response.data;
  }

  async importProhibitedSites(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.api.post(
      "/company/prohibited-sites/import",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  // Enrollment Token for Agent Installation
  async getEnrollmentToken(): Promise<{
    success: boolean;
    data: { enrollment_token: string; download_url: string };
  }> {
    const response = await this.api.get("/company/enrollment-token");
    return response.data;
  }

  async regenerateEnrollmentToken(): Promise<{
    success: boolean;
    message: string;
    data: { enrollment_token: string; download_url: string };
  }> {
    const response = await this.api.post(
      "/company/enrollment-token/regenerate"
    );
    return response.data;
  }

  getAgentDownloadUrl(): string {
    const apiBaseUrl = getApiBaseUrl();
    return `${apiBaseUrl}/api/agent/download`;
  }

  async getAgentVersion(): Promise<{ success: boolean; version: string }> {
    const response = await this.api.get('agent/version');
    return response.data;
  }

  // Reports
  async getReports(params?: {
    start_date?: string;
    end_date?: string;
    branch_id?: string;
    machine_id?: string;
  }): Promise<any> {
    const response = await this.api.get("/reports/all", { params });
    return response.data;
  }

  async getProductivityReport(params?: {
    start_date?: string;
    end_date?: string;
    branch_id?: string;
    machine_id?: string;
  }): Promise<any> {
    const response = await this.api.get("/reports/productivity", { params });
    return response.data;
  }

  async getWebNavigationReport(params?: {
    start_date?: string;
    end_date?: string;
    branch_id?: string;
    machine_id?: string;
  }): Promise<any> {
    const response = await this.api.get("/reports/web-navigation", { params });
    return response.data;
  }

  async getInventoryReport(params?: {
    branch_id?: string;
    machine_id?: string;
  }): Promise<any> {
    const response = await this.api.get("/reports/inventory", { params });
    return response.data;
  }

  async getSystemReport(params?: {
    start_date?: string;
    end_date?: string;
    branch_id?: string;
    machine_id?: string;
  }): Promise<any> {
    const response = await this.api.get("/reports/system", { params });
    return response.data;
  }

  async getOffHoursUsageReport(params?: {
    start_date?: string;
    end_date?: string;
    branch_id?: string;
    machine_id?: string;
  }): Promise<any> {
    const response = await this.api.get("/reports/off-hours-usage", { params });
    return response.data;
  }

  async deleteCompanyBackground(): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete("/company/settings/background");
    return response.data;
  }
}

export const apiService = new ApiService();
